// Lib
import memoizeOne from 'memoize-one';

// Utils
import * as pointLib from '../../../../common/maths/geometry/point';
import { radiansToDegrees } from '../../../../common/maths/trigonometry';
import {
    calculatePositionDelta,
    calculateRotation,
    getDesiredShadowIntensity,
    getNextShadowIntensityGrowing,
    NO_ROTATION,
} from '../../../element/dnd/utils/elementDragAnimationUtils';
import { getDragDimension } from '../../../element/dnd/dragAndDropStateSingleton';

// Types
import { Point } from '../../../../common/maths/geometry/pointTypes';
import { MNElement } from '../../../../common/elements/elementModelTypes';
import { DragAndDropState, DragAndDropInterpolationStyle } from '../dragLayerStateTypes';

const NO_CHANGE = { x: 0, y: 0 };

/**
 * Gets the state updates if no interpolation is used.
 */
const getNoneInterpolatedState = (limitedSourceClientOffset: Point, scaledCustomDragOffset: Point) => ({
    animatingDrag: true,
    groupPosition: pointLib.translate(limitedSourceClientOffset, scaledCustomDragOffset),
    interpolationStyle: DragAndDropInterpolationStyle.NONE,
    elementRotation: NO_ROTATION,
    shadowIntensity: 1,
    desiredShadowIntensity: 1,
    targetOffset: NO_CHANGE,
    maxRotation: 0,
    positionDelta: NO_CHANGE,
});

/**
 * Determines the maximum rotation of an element.
 * This is dependent on the size of the object.  Because a rotation of X degrees looks a lot more dramatic
 * for large elements compared to small elements.  This algorithm restricts the rotation so that no element
 * can end up being rotated more than 55px in the z-direction.
 */
const getMaximumRotation = memoizeOne(
    (shouldRotateElements: boolean, element: MNElement, maxDimension: number): number => {
        if (!shouldRotateElements || !element) return 20;

        if (!maxDimension) return 20;

        // Very large elements behave weird under rotation - disable entirely
        if (maxDimension > 1000) return 0;

        // Causes a normal width card to be restricted to ~20 degrees of rotation
        // This equates to a 55px z limit
        // Also prevents any element from being rotated more than 25 degrees
        const maxRotation = radiansToDegrees(Math.asin(110 / maxDimension));
        return Math.min(maxRotation, 25);
    },
);

/**
 * Gets the state updates if 3D or 2D interpolation is used.
 */
const getMultiDimensionInterpolatedState = (
    dragAndDropInterpolationStyle: DragAndDropInterpolationStyle,
    previousState: DragAndDropState,
    element: MNElement,
    limitedSourceClientOffset: Point,
    movementOffset: Point,
    scaledCustomDragOffset: Point,
    isTouchDevice: boolean,
    dt: number,
) => {
    const shouldRotateElements = dragAndDropInterpolationStyle === DragAndDropInterpolationStyle.THREE_D;

    const desiredShadowIntensity = getDesiredShadowIntensity(
        previousState.shadowIntensity,
        movementOffset,
        isTouchDevice,
    );

    // Ensure that the shadow doesn't immediately jump to 100%
    const nextShadowIntensity = getNextShadowIntensityGrowing(
        previousState.shadowIntensity,
        desiredShadowIntensity,
        isTouchDevice,
    );

    // Linearly interpolate the position to smooth out the motion
    const positionDelta = calculatePositionDelta({
        // @ts-ignore TS can't deal with currying in JS files
        targetPosition: pointLib.translate(limitedSourceClientOffset, scaledCustomDragOffset),
        previousPosition: previousState.groupPosition,
        shouldRotateElements,
        dt,
    });

    // Position the dragged elements to the new location
    const updatedGroupPosition = previousState.groupPosition
        ? // @ts-ignore TS can't deal with currying in JS files
          pointLib.translate(previousState.groupPosition, positionDelta)
        : limitedSourceClientOffset;

    // Determine the maximum location based on the size of the dragged element
    const maxDimension = getDragDimension();
    const maxRotation = getMaximumRotation(shouldRotateElements, element, maxDimension);

    // Determine the rotation of the dragged element
    const updatedElementRotation = shouldRotateElements
        ? calculateRotation({ positionDelta, previousRotation: previousState.elementRotation, maxRotation })
        : NO_ROTATION;

    // This is used when continuing the animation in the canvas element when the element is dropped
    // When the drop occurs, we only know the mouse position where the mouse-up happened, via React DND.
    // Thus, we need to account for the fact that the drag layer element wasn't positioned at the mouse
    // cursor due to the animation and the linear interpolation of the position.
    // @ts-ignore TS can't deal with currying in JS files
    let targetOffset = pointLib.reverseTranslate(limitedSourceClientOffset, updatedGroupPosition);
    // @ts-ignore TS can't deal with currying in JS files
    targetOffset = pointLib.reverseTranslate(scaledCustomDragOffset, targetOffset);

    return {
        animatingDrag: true,
        groupPosition: updatedGroupPosition,
        elementRotation: updatedElementRotation,
        shadowIntensity: nextShadowIntensity,
        targetOffset,
        maxRotation,
        positionDelta,
        desiredShadowIntensity,
    };
};

/**
 * Gets the state updates after interpolation is applied.
 */
export const getInterpolatedState = (
    dragAndDropInterpolationStyle: DragAndDropInterpolationStyle,
    previousState: DragAndDropState,
    element: MNElement,
    limitedSourceClientOffset: Point,
    movementOffset: Point,
    scaledCustomDragOffset: Point,
    isTouchDevice: boolean,
    dt: number,
) => {
    switch (dragAndDropInterpolationStyle) {
        case DragAndDropInterpolationStyle.NONE:
            return getNoneInterpolatedState(limitedSourceClientOffset, scaledCustomDragOffset);
        default:
            return getMultiDimensionInterpolatedState(
                dragAndDropInterpolationStyle,
                previousState,
                element,
                limitedSourceClientOffset,
                movementOffset,
                scaledCustomDragOffset,
                isTouchDevice,
                dt,
            );
    }
};
